import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  Snackbar,
  Tooltip,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  MarkEmailRead as MarkReadIcon,
  DeleteSweep as DeleteSweepIcon,
} from "@mui/icons-material";
import { MaterialReactTable } from "material-react-table";
import { MRT_Localization_VI } from "material-react-table/locales/vi";
import { notificationService } from "services/notification.service";
import { usePageTitle } from "context/PageTitleContext";
import { AppFilterBar } from "components/common/filters";
import { AppButton } from "components/common/buttons";
import { AppSelect, AppDatePicker } from "components/common/inputs";
import DataTable from "components/common/tables/DataTable";
import { formatDateTime, } from "utils/formatters";

const AllNotificationsPage = () => {
  const { setPageTitle } = usePageTitle();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  const priorityOptions = [
    { value: "", label: "Tất cả" },
    { value: "4", label: "Khẩn cấp" },
    { value: "3", label: "Cao" },
    { value: "2", label: "Bình thường" },
    { value: "1", label: "Thấp" },
  ];
  const typeOptions = [
    { value: "", label: "Tất cả" },

    { value: "SYSTEM", label: "Hệ thống" },

    { value: "CONTRACT_EXPIRY", label: "Hết hạn hợp đồng" },
    { value: "CONTRACT_ASSIGNED", label: "Gán hợp đồng" },
    { value: "CONTRACT_UPDATED", label: "Cập nhật hợp đồng" },
    { value: "EVENT_REMINDERS", label: "Sự kiện lịch biểu" },

    { value: "SUBCONTRACT_ASSIGNED", label: "Gán giao khoán" },
    { value: "SUBCONTRACT_UPDATED", label: "Cập nhật giao khoán" },
    { value: "SUBCONTRACT_EXPIRY", label: "Hết hạn giao khoán" },
    { value: "CONTRACT_DELETED", label: "Xóa hợp đồng" },
    { value: "SUBCONTRACT_APPROVAL_REQUESTED", label: "Yêu cầu phê duyệt giao khoán" },
    { value: "SUBCONTRACT_ACCEPTANCE_APPROVAL_REQUESTED", label: "Yêu cầu nghiệm thu giao khoán" },
    { value: "SUBCONTRACT_APPROVAL_REVISION_REQUESTED", label: "Yêu cầu sửa đổi phê duyệt giao khoán" },
    { value: "SUBCONTRACT_ACCEPTANCE_APPROVAL_REVISION_REQUESTED", label: "Yêu cầu sửa đổi duyệt nghiệm thu giao khoán" },
    { value: "SUBCONTRACT_APPROVAL_APPROVED", label: "Duyệt giao khoán" },
    { value: "SUBCONTRACT_ACCEPTANCE_APPROVAL_APPROVED", label: "Duyệt nghiệm thu giao khoán" },

  ];
  const statusOptions = [
    { value: "", label: "Tất cả" },
    { value: "false", label: "Chưa đọc" },
    { value: "true", label: "Đã đọc" },
  ];

  // Set page title when component mounts
  useEffect(() => {
    setPageTitle("Trung tâm thông báo");
  }, [setPageTitle]);
  const [totalCount, setTotalCount] = useState(0);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10, // Đổi từ 20 về 10 theo yêu cầu
  });
  const [globalFilter, setGlobalFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [isReadFilter, setIsReadFilter] = useState("");
  const [rowSelection, setRowSelection] = useState({});
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // State for delete confirmation modal
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({
    open: false,
    selectedIds: [],
    selectedCount: 0,
  });

  // Reset page to first when filters change
  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  }, [priorityFilter, typeFilter, isReadFilter, globalFilter]);

  // Show alert
  const showAlert = (message, severity = "success") => {
    setAlert({ open: true, message, severity });
  };

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const result = await notificationService.getNotifications(
        pagination.pageIndex + 1,
        pagination.pageSize,
        {
          search: globalFilter,
          priority: priorityFilter,
          type: typeFilter,
          isRead: isReadFilter,
        }
      );

      setNotifications(result.notifications);
      setTotalCount(result.totalRecords || result.totalCount || 0);
    } catch (error) {
      console.error("Error fetching notifications:", error);
      showAlert("Có lỗi khi tải thông báo", "error");
    } finally {
      setLoading(false);
    }
  }, [
    pagination.pageIndex,
    pagination.pageSize,
    globalFilter,
    priorityFilter,
    typeFilter,
    isReadFilter,
  ]);

  // Load data on component mount and when filters change
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Mark notification as read
  const handleMarkAsRead = useCallback(
    async (notificationId) => {
      try {
        await notificationService.markAsRead(notificationId);
        setNotifications((prev) =>
          prev.map((notif) =>
            notif.notificationId === notificationId
              ? { ...notif, isRead: true }
              : notif
          )
        );

        // Cập nhật rowSelection để loại bỏ ID đã đánh dấu đọc (vì nút đánh dấu đã đọc sẽ biến mất)
        setRowSelection((prev) => {
          const newSelection = { ...prev };
          delete newSelection[notificationId.toString()];
          return newSelection;
        });

        showAlert("Đã đánh dấu thông báo đã đọc");

        // Reload dữ liệu để đảm bảo đồng bộ
        fetchNotifications();
      } catch (error) {
        showAlert("Có lỗi khi đánh dấu thông báo", "error");
      }
    },
    [fetchNotifications]
  );

  // Delete notification
  const handleDelete = useCallback(async (notificationId) => {
    try {
      await notificationService.deleteNotification(notificationId);
      setNotifications((prev) =>
        prev.filter((notif) => notif.notificationId !== notificationId)
      );
      setTotalCount((prev) => prev - 1);

      // Cập nhật rowSelection để loại bỏ ID đã xóa
      setRowSelection((prev) => {
        const newSelection = { ...prev };
        delete newSelection[notificationId.toString()];
        return newSelection;
      });

      showAlert("Đã xóa thông báo");
    } catch (error) {
      showAlert("Có lỗi khi xóa thông báo", "error");
    }
  }, []);

  // Mark selected as read
  const handleMarkSelectedAsRead = async () => {
    const selectedIds = Object.keys(rowSelection);
    if (selectedIds.length === 0) return;

    try {
      await Promise.all(
        selectedIds.map((id) => notificationService.markAsRead(id))
      );

      // Cập nhật state với việc chuyển đổi đúng kiểu dữ liệu
      setNotifications((prev) => {
        const updated = prev.map((notif) => {
          const shouldUpdate = selectedIds.includes(
            notif.notificationId.toString()
          );

          return shouldUpdate ? { ...notif, isRead: true } : notif;
        });

        return updated;
      });

      setRowSelection({});
      showAlert(`Đã đánh dấu ${selectedIds.length} thông báo đã đọc`);

      // Reload dữ liệu để đảm bảo đồng bộ
      setTimeout(() => {
        fetchNotifications();
      }, 500);
    } catch (error) {
      showAlert("Có lỗi khi đánh dấu thông báo", "error");
    }
  };

  // Delete selected notifications
  const handleDeleteSelected = async () => {
    const selectedIds = Object.keys(rowSelection);
    if (selectedIds.length === 0) return;

    // Mở modal xác nhận thay vì window.confirm
    setDeleteConfirmModal({
      open: true,
      selectedIds: selectedIds,
      selectedCount: selectedIds.length,
    });
  };

  // Xác nhận xóa từ modal
  const handleConfirmDelete = async () => {
    const { selectedIds } = deleteConfirmModal;

    try {
      await Promise.all(
        selectedIds.map((id) => notificationService.deleteNotification(id))
      );
      setNotifications((prev) =>
        prev.filter(
          (notif) => !selectedIds.includes(notif.notificationId.toString())
        )
      );
      setTotalCount((prev) => prev - selectedIds.length);
      setRowSelection({});
      setDeleteConfirmModal({ open: false, selectedIds: [], selectedCount: 0 });
      showAlert(`Đã xóa ${selectedIds.length} thông báo`);
    } catch (error) {
      showAlert("Có lỗi khi xóa thông báo", "error");
    }
  };

  // Hủy xóa từ modal
  const handleCancelDelete = () => {
    setDeleteConfirmModal({ open: false, selectedIds: [], selectedCount: 0 });
  };

  // Get priority color
  const getPriorityColor = useCallback((priority) => {
    switch (priority) {
      case 4:
        return "error";
      case 3:
        return "warning";
      case 2:
        return "primary";
      case 1:
        return "default";
      default:
        return "default";
    }
  }, []);

  // Get priority label
  const getPriorityLabel = useCallback((priority) => {
    switch (priority) {
      case 4:
        return "Khẩn cấp";
      case 3:
        return "Cao";
      case 2:
        return "Bình thường";
      case 1:
        return "Thấp";
      default:
        return "Bình thường";
    }
  }, []);

  // Define columns for Material React Table
  const columns = useMemo(
    () => [
      {
        accessorKey: "title", header: "Tiêu đề", size: 200, enableSorting: false,
      },
      {
        accessorKey: "message", header: "Nội dung", size: 300, enableSorting: false,
      },
      {
        accessorKey: "priority", header: "Độ ưu tiên", size: 70, enableSorting: false,
        Cell: ({ row }) => (
          <Chip
            label={getPriorityLabel(row.original.priority)}
            color={getPriorityColor(row.original.priority)}
            size="small"
            sx={{ fontSize: "0.7rem", height: 20 }}
          />
        ),
      },
      {
        accessorKey: "createdAt", header: "Thời gian", size: 140, enableSorting: false,
        Cell: ({ cell }) => (
          <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
            {formatDateTime(cell.getValue())}
          </Typography>
        ),
      },
      {
        accessorKey: "isRead", header: "Trạng thái", size: 70, enableSorting: false,
        Cell: ({ cell }) => (
          <Chip
            label={cell.getValue() ? "Đã đọc" : "Chưa đọc"}
            color={cell.getValue() ? "success" : "warning"}
            variant={cell.getValue() ? "outlined" : "filled"}
            size="small"
          />
        ),
      },
      {
        accessorKey: "actions", header: "Thao tác", enableSorting: false, size: 70,
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", gap: 0.5, alignItems: 'center', justifyContent: 'center' }}>
            {!row.original.isRead && (
              <Tooltip title="Đánh dấu đã đọc">
                <IconButton
                  size="small"
                  onClick={() => handleMarkAsRead(row.original.notificationId)}
                >
                  <MarkReadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Xóa">
              <IconButton
                size="small"
                onClick={() => handleDelete(row.original.notificationId)}
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [
      handleMarkAsRead,
      handleDelete,
      getPriorityLabel,
      getPriorityColor,
      formatDateTime,
    ]
  );

  return (
    <Box>
      {/* Filter Controls */}
      <Box sx={{ px: 2, pt: 1 }}>
        <AppFilterBar icon={false}>
          <AppSelect
            value={priorityFilter}
            label="Độ ưu tiên"
            options={priorityOptions}
            onChange={(value) => {
              setPriorityFilter(value);
            }}
            sx={{ minWidth: 180, flex: 1 }}
          />
          <AppSelect
            label="Loại"
            value={typeFilter}
            options={typeOptions}
            onChange={(value) => {
              setTypeFilter(value);
            }}
            sx={{ minWidth: 180, flex: 1 }}
          />
          <AppSelect
            label="Trạng thái"
            value={isReadFilter}
            options={statusOptions}
            onChange={(value) => {
              setIsReadFilter(value);
            }}
            sx={{ minWidth: 180, flex: 1 }}
          />
        </AppFilterBar>
      </Box>

      {/* Data Table */}
      <Paper sx={{ mx: 2 }}>
        <DataTable
          columns={columns}
          data={notifications}
          enableRowSelection={true}
          rowCount={totalCount}
          localization={MRT_Localization_VI}
          getRowId={(row) => row.notificationId}
          state={{
            isLoading: loading,
            pagination,
            globalFilter,
            rowSelection,
          }}
          onPaginationChange={setPagination}
          onGlobalFilterChange={setGlobalFilter}
          onRowSelectionChange={setRowSelection}
          renderTopToolbarCustomActions={() => (
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              {Object.keys(rowSelection).length > 0 && (
                <>
                  <AppButton
                    variant="outlined"
                    startIcon={<MarkReadIcon />}
                    onClick={handleMarkSelectedAsRead}
                  >
                    Đánh dấu đã đọc ({Object.keys(rowSelection).length})
                  </AppButton>
                  <AppButton
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteSweepIcon />}
                    onClick={handleDeleteSelected}
                  >
                    Xóa ({Object.keys(rowSelection).length})
                  </AppButton>
                </>
              )}
            </Box>
          )}
        />
      </Paper>

      {/* Alert Snackbar */}
      <Snackbar
        open={alert.open}
        autoHideDuration={3000}
        onClose={() => setAlert({ ...alert, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setAlert({ ...alert, open: false })}
          severity={alert.severity}
          sx={{ width: "100%" }}
        >
          {alert.message}
        </Alert>
      </Snackbar>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={deleteConfirmModal.open}
        onClose={handleCancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Xác nhận xóa thông báo
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Bạn có chắc chắn muốn xóa {deleteConfirmModal.selectedCount} thông
            báo đã chọn?
            <br />
            Hành động này không thể hoàn tác.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <AppButton onClick={handleCancelDelete} variant="outlined">
            Hủy
          </AppButton>
          <AppButton
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            Xóa
          </AppButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AllNotificationsPage;
